//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features/index';
import {
  IStateParamsService,
  IStateService,
  IUrlRouterProvider,
} from 'angular-ui-router';
import stubTrue from 'lodash/fp/stubTrue';

import Cuid from '@models/Cuid';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

// Sets default values for state data
function FacebookStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess || stubTrue;
  const data = {
    title: 'Facebook',
    navGroup: NavGroup.PUBLISHING,
    ...state.data,
    hasAccess: () =>
      hasAllFeatures(FeatureNames.SocialDashboards) && accessFn(),
  };
  return StateFactory({
    ...state,
    data,
  });
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/facebook', '/facebook/summary');
}

export const states = {
  facebook: FacebookStateFactory({
    url: '/facebook',
    template: componentNameToTemplate('facebookPage'),
    resolve: {
      ...lazyLoadModule('curalate.facebook', () => import('./FacebookApp')),
    },
  }),

  'facebook.summary': FacebookStateFactory({
    url: '/summary',
    views: {
      'facebook-main': {
        template: componentNameToTemplate('facebookSummary'),
      },
    },
    data: {
      title: 'Summary',
    },
  }),

  'facebook.schedule': FacebookStateFactory({
    url: '/schedule?:targetCuid&:copiedScheduledPostId&:copiedDraftId&:postTimeMs&:targetScheduledPostId&:targetDraftId',
    params: {
      copiedSchedulePostId: undefined,
      copiedDraftId: undefined,
      postTimeMs: undefined,
      targetCuid: null,
      targetScheduledPostId: null,
      targetDraftId: undefined,
    },
    views: {
      'facebook-main': {
        template: `<facebook-schedule
                    class="flex-tall cu-w-100"
                    u-page-scroll-to-top
                    target-cuid="::targetCuid"
                    target-scheduled-post-id="::targetScheduledPostId"
                    target-draft-id="::targetDraftId"
                    copied-draft-id="::copiedDraftId"
                    copied-scheduled-post-id="::copiedScheduledPostId"
                    post-time-ms="::postTimeMs"
                ></facebook-schedule>`,
        controller: [
          '$scope',
          '$state',
          '$stateParams',
          (
            $scope: Record<string, unknown>,
            $state: IStateService,
            $stateParams: IStateParamsService,
          ) => {
            $scope.targetCuid = Cuid($stateParams.targetCuid as string);
            $scope.targetScheduledPostId = $stateParams.targetScheduledPostId;
            $scope.targetDraftId = $stateParams.targetDraftId;
            $scope.copiedScheduledPostId = $stateParams.copiedScheduledPostId;
            $scope.copiedDraftId = $stateParams.copiedDraftId;
            $scope.postTimeMs = $stateParams.postTimeMs;
          },
        ],
      },
    },
    data: {
      title: 'Schedule',
    },
  }),

  'facebook.audience': FacebookStateFactory({
    url: '/audience',
    views: {
      'facebook-main': {
        template: componentNameToTemplate('facebookAudience'),
      },
    },
    data: {
      title: 'Audience',
    },
  }),

  'facebook.engagement': FacebookStateFactory({
    url: '/engagement',
    views: {
      'facebook-main': {
        template: componentNameToTemplate('facebookEngagement'),
      },
    },
    data: {
      title: 'Engagement',
    },
  }),

  'facebook.drafts': FacebookStateFactory({
    url: '/drafts',
    views: {
      'facebook-main': {
        template: componentNameToTemplate('facebookDrafts'),
      },
    },
    data: {
      title: 'Facebook Drafts',
      navGroup: NavGroup.PUBLISHING,
    },
  }),
};
